import type { PropsWithChildren } from "react";
import React, { forwardRef, useRef } from "react";
import { styled } from "goober";

import globals from "@/util/global-whitelist.mjs";
import { useClientLayoutEffect } from "@/util/use-client-layout-effect.mjs";

const SCALE = 1.05;
const PERSPECTIVE = "600px";
const TILT_ANGLE = 8;
const DELAY = 150;

const TiltContainer = styled("div", forwardRef)`
  transform-origin: center;
  position: relative;
  z-index: 1;

  &:hover::after {
    opacity: 1;
  }

  --x: 50%;
  --y: 50%;

  &::after {
    position: absolute;
    pointer-events: none;
    content: " ";
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background: radial-gradient(
      ellipse 100% 100% at var(--x) var(--y),
      var(--shade0-15),
      hsla(var(--shade0-hsl) / 0)
    );
    opacity: 0;
    transition: 0.2s opacity ease;
    z-index: 10;
  }
`;

/**
 * Homegrown tilting wrapper. This one should be kind of performance optimized.
 */
export default function Tilting({
  tiltEnable,
  children,
  href,
  ...props
}: PropsWithChildren<{
  tiltEnable?: boolean;
  className?: string;
  href?: string;
}>) {
  const el = useRef(null);

  useClientLayoutEffect(() => {
    if (!el.current || tiltEnable === false) {
      return;
    }
    const tiltElem = el.current;

    function onMouseMove({ clientX: mx, clientY: my }) {
      if (globals.document.body.classList.contains("disable-animations"))
        return;
      const { x, y, width, height } = tiltElem.getBoundingClientRect();
      const cx = x + width / 2;
      const cy = y + height / 2;
      const dx = mx - cx;
      const dy = my - cy;
      const rad = Math.atan2(-dy, dx);
      const cornerDistance = Math.sqrt(
        Math.pow(width / 2, 2) + Math.pow(height / 2, 2),
      );
      const len = Math.min(Math.sqrt(dx * dx + dy * dy) / cornerDistance, 1);
      const rx = Math.sin(rad);
      const ry = Math.cos(rad);
      tiltElem.style.setProperty("--x", `${(0.5 - dx / (width / 2)) * 100}%`);
      tiltElem.style.setProperty("--y", `${(0.5 - dy / (height / 2)) * 100}%`);
      tiltElem.style.transform = `scale(${SCALE}) perspective(${PERSPECTIVE}) rotate3d(${rx}, ${ry}, 0, ${
        len * TILT_ANGLE
      }deg)`;
    }

    let setTransition;
    function onMouseOver() {
      tiltElem.style.transition = `transform ${DELAY}ms ease-out`;
      setTransition = setTimeout(() => {
        tiltElem.style.transition = "";
      }, DELAY);
    }

    function onMouseOut() {
      tiltElem.style.transform = "";
      tiltElem.style.transition = `transform ${DELAY}ms ease-in`;
      clearTimeout(setTransition);
    }

    tiltElem.addEventListener("mouseover", onMouseOver);
    tiltElem.addEventListener("mousemove", onMouseMove);
    tiltElem.addEventListener("mouseout", onMouseOut);

    return () => {
      tiltElem.removeEventListener("mouseover", onMouseOver);
      tiltElem.removeEventListener("mousemove", onMouseMove);
      tiltElem.removeEventListener("mouseout", onMouseOut);
    };
  }, [el, tiltEnable]);

  return (
    <TiltContainer ref={el} className="tilt-container" {...props}>
      {href ? <a href={href}>{children}</a> : children}
    </TiltContainer>
  );
}
